// Bricks design system — shared tokens + primitives
import { createContext, useContext } from 'react';
import { useShellDark } from './useShellDark';

// Every ink/moon text token is at least 4.5:1 on paper, paperSoft, night and nightSoft
// (WCAG AA body text). Check with a contrast tool before changing one.
export const T = {
  paper:'#F4F1EB', paperSoft:'#EDE8DF', paperDeep:'#E4DED2',
  hair:'#1411100F', hairStrong:'#14111020', hairMid:'#14111018',
  ink:'#14110E', inkSoft:'#3B332A', inkMute:'#6B6054', inkFaint:'#736759',
  night:'#141210', nightSoft:'#1C1A17', nightDeep:'#23201C',
  nightHair:'#F4F1EB14', nightHairS:'#F4F1EB26',
  moon:'#F4F1EB', moonSoft:'#D6CFC2', moonMute:'#A0978A', moonFaint:'#8F8678',
  brick:'#D2542B', brickDeep:'#A8401F', brickSoft:'#F4D9CC', brickTint:'#FBEFE8',
  ok:'#4A6B3C', okTint:'#E6ECDD',
  warn:'#B8741F', warnTint:'#F6E8D1',
  bad:'#8C2F24', badTint:'#F1DAD4',
};

export const F = {
  display:'"General Sans",ui-sans-serif,-apple-system,BlinkMacSystemFont,sans-serif',
  mono:'"IoskeleyMono",ui-monospace,"SF Mono",Menlo,monospace',
};

export function th(dark) {
  return {
    bg:    dark ? T.night     : T.paper,
    surf:  dark ? T.nightSoft : '#FFFFFF',
    surf2: dark ? T.nightDeep : T.paperSoft,
    hair:  dark ? T.nightHair : T.hair,
    hairS: dark ? T.nightHairS: T.hairStrong,
    ink:   dark ? T.moon      : T.ink,
    ink2:  dark ? T.moonSoft  : T.inkSoft,
    ink3:  dark ? T.moonMute  : T.inkMute,
    ink4:  dark ? T.moonFaint : T.inkFaint,
    // Attention text (overdue, needs a visit). brickDeep is 3:1 on night, so night lifts it.
    attn:  dark ? '#E07A55'   : T.brickDeep,
  };
}

export function BrickMark({ size=20, color=T.brick, style={} }) {
  // 24×24 viewBox: stem-top (w=11, wider), stem-base (w=6, narrow), bowl (w=10.8)
  // Silhouette reads as lowercase 'b'. Top brick opacity 0.82.
  const h = Math.round(size * 1.28);
  return (
    <svg width={h} height={h} viewBox="0 0 24 24" fill="none" style={style}>
      <rect x="3"    y="4"    width="11"  height="6.4" rx="0.8" fill={color} opacity="0.82" />
      <rect x="3"    y="11.6" width="6"   height="8.4" rx="0.8" fill={color} />
      <rect x="10.2" y="11.6" width="10.8" height="8.4" rx="0.8" fill={color} />
    </svg>
  );
}

export function Wordmark({ size=18, color, style={} }) {
  const c = color || T.ink;
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:Math.round(size*0.5), ...style }}>
      <BrickMark size={size} color={T.brick} />
      <span style={{
        fontFamily:F.display, fontSize:size, fontWeight:500,
        letterSpacing:-size*0.035, color:c, lineHeight:1,
      }}>bricks</span>
    </div>
  );
}

export function Over({ children, color, style={} }) {
  return (
    <span style={{
      fontFamily:F.display, fontSize:13, fontWeight:500,
      letterSpacing:0,
      color: color || T.inkMute, ...style,
    }}>{children}</span>
  );
}

export function Img({ w='100%', h=160, label, dark: darkProp, style={}, r=2 }) {
  const autoDark = useShellDark();
  const dark = darkProp === undefined ? autoDark : darkProp;
  const bg = dark ? T.nightSoft : T.paperDeep;
  const stripe = dark ? T.nightHair : T.hair;
  const fg = dark ? T.moonMute : T.inkMute;
  return (
    <div style={{
      width:w, height:h, background:bg,
      backgroundImage:`repeating-linear-gradient(135deg, transparent 0 10px, ${stripe} 10px 11px)`,
      borderRadius:r, display:'flex', alignItems:'center', justifyContent:'center',
      fontFamily:F.display, fontSize:12, letterSpacing:0,
      color:fg, ...style,
    }}>{label}</div>
  );
}

const _icons = {
  home:<><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9v11h5v-6h4v6h5V9"/></>,
  cal:<><rect x="3" y="5" width="18" height="16" rx="1"/><path d="M3 10h18M8 3v4M16 3v4"/></>,
  job:<><rect x="3" y="6" width="18" height="14" rx="1"/><path d="M8 6V4h8v2M3 12h18"/></>,
  cam:<><path d="M4 7h3l2-2h6l2 2h3v13H4z"/><circle cx="12" cy="13" r="4"/></>,
  user:<><circle cx="12" cy="8" r="4"/><path d="M4 21c1.5-4 4.5-6 8-6s6.5 2 8 6"/></>,
  up:<><path d="M12 16V4M6 10l6-6 6 6"/><path d="M4 20h16"/></>,
  map:<><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2z"/><path d="M9 4v14M15 6v14"/></>,
  pin:<><path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z"/><circle cx="12" cy="9" r="2.5"/></>,
  arr:<path d="M5 12h14M13 6l6 6-6 6"/>,
  arrU:<path d="M12 19V5M6 11l6-6 6 6"/>,
  arrD:<path d="M12 5v14M18 13l-6 6-6-6"/>,
  arrL:<path d="M19 12H5M11 6l-6 6 6 6"/>,
  chk:<path d="M4 12l5 5L20 6"/>,
  plus:<><path d="M12 5v14M5 12h14"/></>,
  cog:<><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></>,
  srch:<><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></>,
  bell:<><path d="M6 17V11a6 6 0 0 1 12 0v6M4 17h16M10 21h4"/></>,
  dol:<><path d="M12 3v18"/><path d="M17 7H9.5a2.5 2.5 0 0 0 0 5H14a2.5 2.5 0 0 1 0 5H6"/></>,
  dots:<><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></>,
  clk:<><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></>,
  trend:<><path d="M3 17l6-6 4 4 8-8"/><path d="M14 7h7v7"/></>,
  cR:<path d="m9 6 6 6-6 6"/>,
  cD:<path d="m6 9 6 6 6-6"/>,
  cU:<path d="m6 15 6-6 6 6"/>,
  grid:<><rect x="3" y="3" width="8" height="8"/><rect x="13" y="3" width="8" height="8"/><rect x="3" y="13" width="8" height="8"/><rect x="13" y="13" width="8" height="8"/></>,
  users:<><circle cx="9" cy="8" r="3.5"/><path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6"/><path d="M16 4.5a3.5 3.5 0 0 1 0 7M22 21c0-3-1.8-5.2-5-6"/></>,
  logout:<><path d="M9 21H5V3h4"/><path d="M16 16l5-4-5-4M9 12h12"/></>,
  sliders:<><path d="M4 6h16M4 12h16M4 18h16"/><circle cx="9" cy="6" r="2"/><circle cx="16" cy="12" r="2"/><circle cx="7" cy="18" r="2"/></>,
  dl:<><path d="M12 4v12M6 10l6 6 6-6"/><path d="M4 20h16"/></>,
  menu:<><path d="M4 6h16M4 12h16M4 18h16"/></>,
  eye:<><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></>,
  x:<><path d="m6 6 12 12M18 6 6 18"/></>,
  cards:<><rect x="3" y="5" width="18" height="14" rx="1"/><path d="M3 10h18"/></>,
  file:<><path d="M5 3h10l4 4v14H5z"/><path d="M15 3v4h4"/></>,
  star:<path d="m12 3 2.8 6.2 6.7.7-5 4.6 1.5 6.6L12 17.7 5.9 21l1.5-6.6-5-4.6 6.7-.7z"/>,
  zap:<path d="M13 2 3 14h7l-1 8 10-12h-7z"/>,
  play:<path d="M7 4v16l13-8z"/>,
  nav:<path d="m3 21 9-18 3 9 6 3z"/>,
  mail:<><rect x="3" y="5" width="18" height="14" rx="1"/><path d="m3 6 9 7 9-7"/></>,
  lock:<><rect x="5" y="11" width="14" height="10" rx="1"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></>,
  heart:<path d="M12 21s-8-5-8-11a5 5 0 0 1 8-3 5 5 0 0 1 8 3c0 6-8 11-8 11z"/>,
  bolt:<path d="M13 2 3 14h7l-1 8 10-12h-7z"/>,
  phone:<path d="M5 3h4l2 5-2 2a12 12 0 0 0 5 5l2-2 5 2v4a2 2 0 0 1-2 2A18 18 0 0 1 3 5a2 2 0 0 1 2-2z"/>,
  // Precision set shared with www.brxs.ca (src/components/icons/icons.ts): 24 grid, square geometry.
  media:<path d="M3 7h5l1.5-2.5h5L16 7h5v12H3zM15.5 13a3.5 3.5 0 1 1-7 0a3.5 3.5 0 1 1 7 0zM18 9.5h1"/>,
  tour3d:<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9zM4 7.5l8 4.5 8-4.5M12 12v9"/>,
  floorPlan:<path d="M3 3h18v18H3zM10 3v8H3M14 21v-7h7M10 15v6M17 3v4"/>,
  siteProgress:<path d="M6 21V4M6 4h14M6 8l4-4M17 4v5M15.5 9h3v2.5h-3zM3 21h18M14 21v-6h4v6"/>,
  booked:<path d="M4 5h16v15H4zM4 9h16M8 3v4M16 3v4M9 14.5l2 2 4-4"/>,
  homeServices:<path d="M3 11l9-7 9 7M5 9.5V20h14V9.5M9 16h6M9 13h6"/>,
  assigned:<path d="M3 12h10M9 8l4 4-4 4M15 4h5v16h-5"/>,
  delivered:<path d="M4 4h16v16H4zM8 12l3 3 5-6"/>,
  layers:<path d="M12 3l9 5-9 5-9-5zM3 12.5l9 5 9-5M3 17l9 5 9-5"/>,
  measure:<path d="M3 17L17 3l4 4L7 21zM7 13l2 2M10 10l2 2M13 7l2 2"/>,
  deal:<path d="M5 3h10l4 4v14H5zM15 3v4h4M8 11h8M8 15h8M8 19h4"/>,
  calendar:<path d="M4 5h16v15H4zM4 9h16M8 3v4M16 3v4M8 13h2v2H8zM14 13h2v2h-2z"/>,
  esign:<path d="M6 15L15 6l3 3-9 9H6zM13 8l3 3M4 21h16"/>,
  mls:<path d="M3 11l9-7 9 7M5 9.5V20h14V9.5M10 20v-6h4v6"/>,
  crm:<path d="M3 5h18v14H3zM7 9h4v4H7zM14 10h4M14 13h4M7 16h11"/>,
  integrations:<path d="M4 4h6v6H4zM14 14h6v6h-6zM10 7h7v7"/>,
  // App-only additions drawn to the same spec (square caps, mitred joins, no fills).
  overview:<path d="M3 3h8v10H3zM13 3h8v6h-8zM13 11h8v10h-8zM3 15h8v6H3z"/>,
  docs:<path d="M7 3h9l4 4v12H7zM16 3v4h4M4 7v14h12M10 11h7M10 14h7"/>,
  compliance:<path d="M12 3l8 3v6c0 4.5-3.4 7.8-8 9-4.6-1.2-8-4.5-8-9V6zM8.5 12l2.5 2.5 4.5-5"/>,
  ledger:<path d="M4 4h16v16H4zM4 9h16M4 14h16M10 4v16"/>,
  report:<path d="M4 20V10M10 20V4M16 20v-7M3 20h18"/>,
  card:<path d="M3 6h18v12H3zM3 10h18M6 15h4"/>,
  team:<path d="M6 8h4v4H6zM14 8h4v4h-4zM3 20v-3h10v3M11 20v-3h10v3"/>,
  person:<path d="M9 4h6v6H9zM5 20v-6h14v6"/>,
  boost:<path d="M4 20L20 4M20 4h-7M20 4v7"/>,
  recruit:<path d="M8 5h5v5H8zM4 20v-6h13v6M19 7v6M16 10h6"/>,
  settings:<path d="M4 7h10M18 7h2M4 17h2M10 17h10M14 4h4v6h-4zM6 14h4v6H6z"/>,
  external:<path d="M14 4h6v6M20 4l-9 9M18 14v6H4V6h6"/>,
  inbox:<path d="M3 13l3-8h12l3 8v6H3zM3 13h5l1 2h6l1-2h5"/>,
  hiring:<path d="M4 7h16v13H4zM9 7V4h6v3M4 12h16"/>,
  prospect:<path d="M10 4a6 6 0 1 1 0 12a6 6 0 1 1 0-12zM14.5 14.5L20 20"/>,
  folder:<path d="M3 5h7l2 2h9v12H3z"/>,
  help:<path d="M4 4h16v16H4zM9.5 9.5a2.5 2.5 0 1 1 3.6 2.24c-.7.35-1.1.96-1.1 1.76V14M12 16.5v.5"/>,
  warn:<path d="M12 4l9 16H3zM12 10v5M12 17.5v.5"/>,
  // Site progress viewer and gallery (same spec).
  scan:<path d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4M12 7l4.5 2.5v5L12 17l-4.5-2.5v-5zM7.5 9.5L12 12l4.5-2.5M12 12v5"/>,
  pointCloud:<path d="M5 12h.5M8.5 7.5h.5M8.5 16.5h.5M12 4.5h.5M12 10h.5M12 14h.5M12 19.5h.5M15.5 7.5h.5M15.5 16.5h.5M19 12h.5"/>,
  elevation:<path d="M3 18l5-6 4 3 4-7 5 10M3 21h18"/>,
  film:<path d="M3 5h18v14H3zM3 8.5h18M3 15.5h18M7 5v3.5M12 5v3.5M17 5v3.5M7 15.5V19M12 15.5V19M17 15.5V19"/>,
  resetView:<path d="M5 12a7 7 0 1 0 2.05-4.95M5 4v4h4"/>,
  fullscreen:<path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5"/>,
  fullscreenExit:<path d="M9 4v5H4M20 9h-5V4M15 20v-5h5M4 15h5v5"/>,
  close:<path d="M6 6l12 12M18 6L6 18"/>,
  download:<path d="M12 4v11M7 10l5 5 5-5M4 15v5h16v-5"/>,
  compare:<path d="M12 3v18M4 6h5v12H4zM15 6h5v12h-5z"/>,
  chevronDown:<path d="M6 9l6 6 6-6"/>,
  chevronLeft:<path d="M15 6l-6 6 6 6"/>,
  chevronRight:<path d="M9 6l6 6-6 6"/>,
  check:<path d="M5 12l4 4 10-10"/>,
  queued:<path d="M4 4h16v16H4zM12 8v4l3 2"/>,
  link:<path d="M10 14l4-4M8.5 11.5L6 14a3 3 0 0 0 4 4l2.5-2.5M15.5 12.5L18 10a3 3 0 0 0-4-4l-2.5 2.5"/>,
  playFrame:<path d="M3 5h18v14H3zM10 9v6l5-3z"/>,
  gallery:<path d="M3 3h8v8H3zM13 3h8v8h-8zM3 13h8v8H3zM13 13h8v8h-8z"/>,
};

export function I({ name, size=18, color='currentColor', sw=1.5, style={} }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth={sw}
      strokeLinecap="square" strokeLinejoin="miter" strokeMiterlimit={10} aria-hidden="true" style={{ flexShrink:0, ...style }}>
      {_icons[name]||null}
    </svg>
  );
}

// Status label. Plain sentence-case text, no bubble: colour only when it needs
// attention (brick / warn / bad tones → muted brick). See docs/ui-standard.md.
export function pillText(children) {
  if (typeof children !== 'string') return children;
  const t = children.replace(/_+/g, ' ').trim();
  if (t && (t === t.toUpperCase() || t === t.toLowerCase())) return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  return t;
}

export function Pill({ children, tone='neutral', dark: darkProp }) {
  const autoDark = useShellDark();
  const dark = darkProp === undefined ? autoDark : darkProp;
  const palette = th(dark);
  const attention = tone === 'brick' || tone === 'warn' || tone === 'bad';
  const color = attention ? (dark ? T.brickSoft : T.brickDeep) : tone === 'ink' ? palette.ink : palette.ink2;
  return (
    <span data-tone={tone} style={{
      color, fontFamily:F.display, fontSize:12.5, fontWeight: attention || tone === 'ink' ? 500 : 400,
      letterSpacing:0, whiteSpace:'nowrap',
    }}>{pillText(children)}</span>
  );
}

// The page's one main action is Bricks orange; every other button stays ink or
// outlined (docs/ui-standard.md). PageHeader wraps its action in MainCtaScope so a
// primary button there renders orange without each page choosing a colour.
export const MainCtaContext = createContext(false);
export function MainCtaScope({ children }) {
  return <MainCtaContext.Provider value={true}>{children}</MainCtaContext.Provider>;
}
export function resolveBtnVariant(variant, inMainCta) {
  return inMainCta && variant === 'primary' ? 'accent' : variant;
}

// dark: omit it to follow the app/system colour scheme; pass true/false to pin a surface.
export function Btn({ children, variant='primary', dark: darkProp, full=false, icon, style={}, as: Comp='button', ...props }) {
  const autoDark = useShellDark();
  const dark = darkProp === undefined ? autoDark : darkProp;
  const palette = th(dark);
  const inMainCta = useContext(MainCtaContext);
  const v = resolveBtnVariant(variant, inMainCta);
  const styles = {
    // Shared button system (www.brxs.ca): 48px, 2px corners. Primary is ink on paper and
    // off-white on night; secondary is a graphite outline.
    primary: { background: dark ? T.moon : T.ink, color: dark ? T.ink : T.paper, border: '1px solid transparent' },
    // Ink on brick is 4.52:1 (AA); white on brick is only 4.16:1.
    accent:  { background: T.brick, color: T.ink, border: '1px solid transparent' },
    ghost:   { background: 'transparent', color: palette.ink, border: `1px solid ${dark ? T.moonFaint : T.inkMute}` },
    secondary:{ background: palette.surf2, color: palette.ink, border: `1px solid ${palette.hair}` },
  };
  // Disabled reads as an intentional, outlined block in both themes (not a greyed-out fill).
  const off = props.disabled ? { background: 'transparent', color: palette.ink3, border: `1px solid ${dark ? 'rgba(244,241,235,0.26)' : 'rgba(20,17,14,0.24)'}`, cursor: 'not-allowed' } : null;
  return (
    <Comp 
      data-variant={v}
      {...props}
      style={{
        ...(styles[v] || styles.primary),
        ...off,
        minHeight:48, padding:'0 20px', borderRadius:2, boxSizing:'border-box',
        fontFamily:F.display, fontSize:14, fontWeight:500, letterSpacing:-0.1,
        display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8,
        width: full ? '100%' : 'auto', cursor: off ? 'not-allowed' : 'pointer', textDecoration:'none',
        ...style,
        ...(off ? { background: off.background, color: off.color, border: off.border } : null),
      }}
    >
      {children}{icon && <I name={icon} size={16} color="currentColor"/>}
    </Comp>
  );
}
